import React, { Component } from "react";
import { Button, Flex, Select, Space, Splitter, Layout, Tooltip, message as antdMessage, Tabs } from "antd";
import {
  CaretRightOutlined,
  SaveOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  ThunderboltOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-mysql";
import "ace-builds/src-noconflict/theme-chrome";
import "ace-builds/src-noconflict/ext-language_tools";
import _ from "lodash";
import { PostRequest, PostRequestResolve } from "../../../utils/FetchHelper";
import { Message } from "../../../variables/general";
import URL from "../../../utils/url";
import SqlTable from "../SqlTable";
import DashboardContext from "../../../common/ContextWrappers/Dashboard/Context";
import withContext from "../../../hoc/withContext";
import { getDashboardHeader } from "../../../utils/globalDashboardUtils";
import { getStatementAtCursor, getAllStatements } from "../../../utils/sqlStatementParser";
import SavedQueriesSidebar from "../SavedQueriesSidebar";
import SaveQueryModal from "../SaveQueryModal";
import "./style.css";

const { Option } = Select;

const ALL_DASHBOARD_OPTION = "all deployments";
const ALL_DATABASE_OPTION = "all tenants";
const ALL_ACTIVE_TENANTS_OPTION = "all active tenants";

class Console extends Component {
  constructor(props) {
    super(props);
    const { isGlobalDashboard } = this.props;
    this.state = {
      editorValue: "",
      isLoading: false,
      database: ALL_ACTIVE_TENANTS_OPTION,
      databaseList: [...(isGlobalDashboard ? [ALL_DASHBOARD_OPTION] : []), ALL_ACTIVE_TENANTS_OPTION, ALL_DATABASE_OPTION],
      isDragging: false,
      // Saved queries feature state
      sidebarCollapsed: false,
      saveModalVisible: false,
      editingQuery: null,
      resultTabs: [], // Array of {key, query, result, columns, timestamp}
      activeResultTab: null,
    };
    this.aceEditorRef = React.createRef();
    this.sidebarRef = React.createRef();
  }

  controller = new window.AbortController();

  componentDidMount() {
    this.getDatabaseList();
    // Add resize observer to handle window resize events
    this.resizeObserver = new ResizeObserver(this.handleResize);
    if (this.aceEditorRef.current) {
      this.resizeObserver.observe(this.aceEditorRef.current.editor.container);
    }
  }

  componentWillUnmount() {
    if (this.controller) this.controller.abort();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getDatabaseList = async () => {
    PostRequest(
      URL.listAccountNames,
      this.controller.signal,
      null,
      (result) => {
        this.setState((prevState) => ({
          ...prevState,
          databaseList: [...prevState.databaseList, ...result],
        }));
      },
      (error) => {
        this.setState((prevState) => ({
          ...prevState,
          isLoading: false,
        }));
        Message.error("Error Listing Databases", _.get(error, "message", ""));
      }
    );
  }

  onChange = (editorValue) => {
    this.setState({ editorValue: editorValue });
  };

  handleResize = () => {
    // Debounce resize to avoid excessive calls
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      if (this.aceEditorRef.current && this.aceEditorRef.current.editor) {
        this.aceEditorRef.current.editor.resize();
      }
    }, 100);
  };

  handleSplitterResize = () => {
    // Handle splitter resize event
    setTimeout(() => {
      if (this.aceEditorRef.current && this.aceEditorRef.current.editor) {
        this.aceEditorRef.current.editor.resize();
        this.aceEditorRef.current.editor.renderer.updateFull();
      }
    }, 16); // Reduced timeout for smoother resize
  };

  handleSplitterResizeStart = () => {
    this.setState({ isDragging: true });
    // Disable editor interactions during drag
    if (this.aceEditorRef.current && this.aceEditorRef.current.editor) {
      this.aceEditorRef.current.editor.setReadOnly(true);
    }
  };

  handleSplitterResizeEnd = () => {
    this.setState({ isDragging: false });
    // Re-enable editor interactions after drag
    if (this.aceEditorRef.current && this.aceEditorRef.current.editor) {
      this.aceEditorRef.current.editor.setReadOnly(false);
      // Force focus back to editor
      setTimeout(() => {
        this.aceEditorRef.current.editor.focus();
      }, 100);
    }
  };

  executeQuery = async (queryText, addToTabs = true) => {
    try {
      this.setState({ isLoading: true });
      const { database } = this.state;
      const { getServerDeploymentName } = this.props;
      let finalResult = [];
      let headerKeys = new Set([]);;
      if (database === ALL_DASHBOARD_OPTION) {
        const { dashboardList } = this.props;
        const promises = [];
        _.forEach(dashboardList, d => {
          const deployment = _.get(d, "details.deploymentInfo.name");
          // if (!deployment) {
          //   return
          // }
          const payload = {
            query: queryText,
            db: ALL_ACTIVE_TENANTS_OPTION,
            serverDeployment: deployment,
          };
          promises.push(PostRequestResolve(
            URL.executeQuery,
            this.controller.signal,
            payload,
            getDashboardHeader(d)
          ));
        })
        headerKeys.add("Dashboard")
        headerKeys.add("Account")
        const results = await Promise.all(promises);
        if (_.isArray(results)) {
          _.forEach(results, (result, i) => {
            if (_.isArray(result)) {
              for (const k of _.keys(_.get(result, "[0]", []))) {
                headerKeys.add(k);
              }
              _.forEach(result, (r, index) => {
                finalResult.push(
                  { "Dashboard": _.get(dashboardList, [i, "name"]), "Row ID": index, ...r }
                );
              });
            } else {
              try {
                for (const k of _.keys(result)) {
                  headerKeys.add(k);
                }
              }
              catch (err) {
                console.log(err);
              }
              finalResult.push(
                {
                  "Dashboard": _.get(dashboardList, [i, "name"]),
                  ...result,
                }
              );
            }
          })
        }
      } else {
        const payload = {
          query: queryText,
          db: database,
          serverDeployment: getServerDeploymentName(),
        };
        const result = await PostRequestResolve(
          URL.executeQuery,
          this.controller.signal,
          payload
        );
        if (result && !_.get(result, "status", true)) {
          throw result
        }
        if (result && Array.isArray(result)) {
          finalResult = result.map((r, index) => {
            return { "Row ID": index, ...r };
          })
        }
        headerKeys.add("Account");
        for (const k of _.keys(_.get(result, "[0]", []))) {
          headerKeys.add(k);
        }
      }
      const columns = [];
      for (const m of headerKeys.keys()) {
        columns.push({
          title: this.titleParser(m),
          dataIndex: m,
          key: m
        });
      }

      const tabKey = `result-${Date.now()}`;
      const truncatedQuery = queryText.length > 50 
        ? queryText.substring(0, 50) + "..." 
        : queryText;
      
      this.setState(prevState => ({
        resultTabs: [
          ...prevState.resultTabs,
          {
            key: tabKey,
            query: queryText,
            label: truncatedQuery,
            result: finalResult,
            columns,
            timestamp: new Date().toLocaleTimeString(),
          }
        ],
        activeResultTab: tabKey,
        isLoading: false,
      }));
    } catch (error) {
      this.setState({
        isLoading: false,
      });
      Message.error("Error Querying DB", _.get(error, "message", ""));
    }
  };

  onExecute = async () => {
    const { editorValue } = this.state;
    if (!editorValue.trim()) {
      antdMessage.warning("Please enter a query");
      return;
    }
    await this.executeQuery(editorValue);
  };

  onExecuteAtCursor = async () => {
    const { editorValue } = this.state;
    if (!editorValue.trim()) {
      antdMessage.warning("Please enter a query");
      return;
    }

    // Get cursor position
    const editor = this.aceEditorRef.current?.editor;
    if (!editor) return;

    const cursorPosition = editor.getCursorPosition();
    const cursorIndex = editor.session.doc.positionToIndex(cursorPosition);

    // Get statement at cursor
    const statement = getStatementAtCursor(editorValue, cursorIndex);
    
    if (!statement || !statement.trimmedText) {
      antdMessage.warning("No statement found at cursor position");
      return;
    }

    // Highlight the statement being executed
    const Range = window.ace.require("ace/range").Range;
    const startPos = editor.session.doc.indexToPosition(statement.start);
    const endPos = editor.session.doc.indexToPosition(statement.end);
    
    const marker = editor.session.addMarker(
      new Range(startPos.row, startPos.column, endPos.row, endPos.column),
      "ace_active-line",
      "text"
    );

    // Execute the statement
    await this.executeQuery(statement.trimmedText);

    // Remove highlight after 1 second
    setTimeout(() => {
      editor.session.removeMarker(marker);
    }, 1000);
  };

  onExecuteAll = async () => {
    const { editorValue } = this.state;
    if (!editorValue.trim()) {
      antdMessage.warning("Please enter a query");
      return;
    }

    const statements = getAllStatements(editorValue);
    
    if (statements.length === 0) {
      antdMessage.warning("No statements found");
      return;
    }

    if (statements.length === 1) {
      await this.executeQuery(statements[0], true);
      return;
    }

    antdMessage.info(`Executing ${statements.length} statements sequentially...`);
    
    for (let i = 0; i < statements.length; i++) {
      const statement = statements[i];
      if (statement.trim()) {
        try {
          await this.executeQuery(statement, true);
          antdMessage.success(`Statement ${i + 1}/${statements.length} completed`);
        } catch (error) {
          antdMessage.error(`Statement ${i + 1} failed: ${error.message}`);
          break;
        }
      }
    }
  };

  toggleSidebar = () => {
    this.setState(prevState => ({
      sidebarCollapsed: !prevState.sidebarCollapsed,
    }));
  };

  handleSaveQuery = () => {
    const { editorValue } = this.state;
    if (!editorValue.trim()) {
      antdMessage.warning("Please enter a query to save");
      return;
    }
    this.setState({
      saveModalVisible: true,
      editingQuery: null,
    });
  };

  handleLoadQuery = (query) => {
    this.setState({ editorValue: query.query });
    antdMessage.success(`Loaded: ${query.name}`);
  };

  handleEditQuery = (query) => {
    this.setState({
      saveModalVisible: true,
      editingQuery: query,
    });
  };

  handleSaveModalClose = () => {
    this.setState({
      saveModalVisible: false,
      editingQuery: null,
    });
  };

  handleQuerySaved = () => {
    // Refresh sidebar
    if (this.sidebarRef.current) {
      this.sidebarRef.current.loadQueries();
    }
  };

  handleTabChange = (activeKey) => {
    this.setState({ activeResultTab: activeKey });
  };

  handleTabClose = (targetKey) => {
    const { resultTabs, activeResultTab } = this.state;
    const newTabs = resultTabs.filter(tab => tab.key !== targetKey);
    
    let newActiveKey = activeResultTab;
    if (targetKey === activeResultTab) {
      // If closing active tab, switch to the last tab
      newActiveKey = newTabs.length > 0 ? newTabs[newTabs.length - 1].key : null;
    }
    
    this.setState({
      resultTabs: newTabs,
      activeResultTab: newActiveKey,
    });
  };

  handleClearAllTabs = () => {
    this.setState({
      resultTabs: [],
      activeResultTab: null,
    });
    antdMessage.success("All result tabs cleared");
  };

  titleParser(str) {
    var splitStr = str.toLowerCase().replaceAll('_', ' ').split(' ');
    for (var i = 0; i < splitStr.length; i++) {
      splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
    }
    return splitStr.join(' ');
  }


  onDbSelect(value) {
    this.setState((prevState) => ({
      ...prevState,
      database: value,
    }));
  }

  render() {
    const {
      editorValue,
      isLoading,
      databaseList,
      isDragging,
      sidebarCollapsed,
      saveModalVisible,
      editingQuery,
      resultTabs,
      activeResultTab,
    } = this.state;

    return (
      <Layout style={{ height: "100%", overflow: "hidden" }}>
        <SavedQueriesSidebar
          ref={this.sidebarRef}
          collapsed={sidebarCollapsed}
          onLoadQuery={this.handleLoadQuery}
          onEditQuery={this.handleEditQuery}
        />
        <Layout style={{ height: "100%", overflow: "hidden" }}>
          <Splitter
            layout="vertical"
            style={{
              height: "100%",
              overflow: "hidden",
              cursor: isDragging ? "row-resize" : "default",
            }}
            onResize={this.handleSplitterResize}
            onResizeStart={this.handleSplitterResizeStart}
            onResizeEnd={this.handleSplitterResizeEnd}
          >
            <Splitter.Panel 
              collapsible={{ start: true, end: true, showCollapsibleIcon: true }}
              style={{ overflow: "hidden" }}
            >
              <Flex style={{ paddingBottom: "8px", gap: "8px" }}>
                <Tooltip title={sidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}>
                  <Button
                    icon={
                      sidebarCollapsed ? (
                        <MenuUnfoldOutlined />
                      ) : (
                        <MenuFoldOutlined />
                      )
                    }
                    onClick={this.toggleSidebar}
                  />
                </Tooltip>
                <span style={{ margin: "auto 8px auto 0px" }}>
                  Select Database
                </span>
                <Select
                  showSearch
                  style={{ width: "max(300px, 50%)" }}
                  placeholder={
                    databaseList.length === 1 ? "Loading..." : "Select Database"
                  }
                  optionFilterProp="children"
                  disabled={databaseList.length === 1}
                  onChange={(e) => this.onDbSelect(e)}
                  defaultValue={ALL_ACTIVE_TENANTS_OPTION}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                    0
                  }
                >
                  {databaseList.map((name) => (
                    <Option key={name} value={name}>
                      {name}
                    </Option>
                  ))}
                </Select>
                <Space style={{ marginLeft: "auto" }}>
                  <Tooltip title="Save Query">
                    <Button
                      icon={<SaveOutlined />}
                      onClick={this.handleSaveQuery}
                    >
                      Save
                    </Button>
                  </Tooltip>
                  <Tooltip title="Run Statement at Cursor (Ctrl+Enter)">
                    <Button
                      type="default"
                      icon={<ThunderboltOutlined />}
                      onClick={this.onExecuteAtCursor}
                      loading={isLoading}
                    >
                      Run
                    </Button>
                  </Tooltip>
                  <Tooltip title="Run All Statements">
                    <Button
                      type="primary"
                      icon={<CaretRightOutlined />}
                      onClick={this.onExecuteAll}
                      loading={isLoading}
                    >
                      Run All
                    </Button>
                  </Tooltip>
                </Space>
              </Flex>
              <AceEditor
                ref={this.aceEditorRef}
                mode="mysql"
                theme="chrome"
                value={editorValue}
                onChange={this.onChange.bind(this)}
                name="console"
                editorProps={{ $blockScrolling: true }}
                width="100%"
                height="100%"
                setOptions={{
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion: true,
                  enableSnippets: true,
                  fontSize: 16,
                  readOnly: isDragging,
                }}
                wrapEnabled
                onLoad={(editor) => {
                  // Ensure editor is properly initialized
                  editor.resize();
                  editor.renderer.updateFull();

                  // Add keyboard shortcut for Ctrl+Enter
                  editor.commands.addCommand({
                    name: "executeAtCursor",
                    bindKey: { win: "Ctrl-Enter", mac: "Command-Enter" },
                    exec: () => {
                      this.onExecuteAtCursor();
                    },
                  });
                }}
              />
            </Splitter.Panel>
            <Splitter.Panel 
              collapsible={{ start: true, end: true, showCollapsibleIcon: true }}
              style={{ overflow: "hidden" }}
            >
              {isLoading ? (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  Executing query...
                </div>
              ) : resultTabs.length === 0 ? (
                <div style={{ 
                  padding: "40px", 
                  textAlign: "center",
                  color: "#8c8c8c"
                }}>
                  No results to display. Execute a query to see results here.
                </div>
              ) : (
                <Tabs
                  hideAdd
                  type="editable-card"
                  size="small"
                  activeKey={activeResultTab}
                  onChange={this.handleTabChange}
                  onEdit={(targetKey, action) => {
                    if (action === 'remove') {
                      this.handleTabClose(targetKey);
                    }
                  }}
                  tabBarExtraContent={
                    resultTabs.length > 0 && (
                      <Tooltip title="Clear all results">
                        <Button
                          type="text"
                          size="small"
                          icon={<ClearOutlined />}
                          onClick={this.handleClearAllTabs}
                          style={{ marginRight: "8px" }}
                        >
                          Clear All
                        </Button>
                      </Tooltip>
                    )
                  }
                  tabBarStyle={{ marginBottom: 0 }}
                  style={{ 
                    height: "100%",
                    display: "flex",
                  }}
                  items={resultTabs.map((tab) => ({
                    key: tab.key,
                    label: (
                      <Tooltip title={tab.query}>
                        <span style={{ fontSize: "11px" }}>
                          {tab.timestamp} - {tab.label}
                        </span>
                      </Tooltip>
                    ),
                    children: (
                      <SqlTable 
                        columns={tab.columns} 
                        data={tab.result}
                        query={tab.query}
                      />
                    ),
                    closable: true,
                  }))}
                />
              )}
            </Splitter.Panel>
          </Splitter>
        </Layout>

        <SaveQueryModal
          open={saveModalVisible}
          currentQuery={editorValue}
          editQuery={editingQuery}
          onClose={this.handleSaveModalClose}
          onSave={this.handleQuerySaved}
        />
      </Layout>
    );
  }
}

export default withContext(Console, DashboardContext);
